import type { Draft } from "immer";

import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import type { EditorChanges } from "@/features/editor/types";
import type { DeepPartial } from "@/features/utils/genericity";
import type { ApiErrorResponse } from "@/libs/profilesAPI";
import { API_SERVER_DOMAINS } from "@/sw/constants";

export const downloadTargetedConfiguration = (data: any, name: string) => {
  const configString = JSON.stringify(data, null, 2);

  // Create downloadable link with JSON profile
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(configString),
  );
  element.setAttribute("download", `${name}.json`);

  element.style.display = "none";
  document.body.appendChild(element);

  //Download!
  element.click();

  document.body.removeChild(element);
};

export const generateURLFromProfile = (profileName: string) => {
  const url = new URL(window.location.href);

  const pathSegments = url.pathname.split("/").filter(Boolean);

  // if there are path variables then replace last one, elsewhere push it
  if (pathSegments.length > 0) {
    pathSegments[pathSegments.length - 1] = profileName;
  } else {
    pathSegments.push(profileName);
  }

  // Remove the dashboard_embed query param if present
  url.searchParams.delete("dashboard_embed");

  url.pathname = `/${pathSegments.join("/")}`;
  return url.toString();
};

// Generates a URL for opening a profile in a new tab
// Removes all parameters from the URL except "domain" if not production
export const generateURLFromProfileForNewTab = (
  profileName: string,
  domain: string,
) => {
  const url = new URL(window.location.href);

  url.search = "";

  url.pathname = profileName;

  // Re-add domain param only if it exists and is NOT production
  const isProductionDomain = API_SERVER_DOMAINS.includes(domain);

  if (domain && !isProductionDomain) {
    url.searchParams.set("domain", domain);
  }

  return url.toString();
};

export const reloadAppByNewProfileName = (newProfileName: string) => {
  window.location.href = generateURLFromProfile(newProfileName);
};

export const isAlphanumericOrUnderscoreOrDash = (value: string): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  return /^[a-z0-9_-]*$/.test(value.toLowerCase());
};

export const getProfileApiErrorMessage = (res: ApiErrorResponse): string => {
  if (res.status === 422) {
    const firstError = res.errors?.at(0);
    if (firstError?.subcode === "name_unavailable") {
      return "The profile name is already in use. Please choose a different name.";
    }
    if (firstError?.message) {
      return firstError.message;
    }
    return "The profile data is invalid. Please review the configuration.";
  }
  return "Profile could not be saved";
};

/**
 * Given a prior set of changes, apply the new changes to the state
 *
 * @param changes - The changes to apply
 * @param draft - The draft state to apply the changes to
 */
export const applyChangesToState = <T extends object>(
  changes: Draft<EditorChanges<T>>,
  draft: Draft<EditorChanges<T>>,
) => {
  Object.entries(changes).forEach(([id, change]) => {
    const { type } = change;

    if (type === "deleted") {
      // If the item was newly added, simply remove it
      if (draft[id]?.type === "new") {
        delete draft[id];
      } else {
        // Otherwise, mark it as deleted
        draft[id] = { type };
      }
    } else {
      const item = (
        change as Extract<typeof change, { type: "new" | "updated" }>
      ).item;

      const existing = draft[id];

      if (type === "new" && !existing) {
        // If no previous item is found, add it to the store
        draft[id] = {
          item: item as Draft<T>,
          type: "new",
        } as unknown as Draft<EditorChanges<T>[string]>;
      } else if (existing?.type === "new") {
        // If the item was newly added, merge the changes but keep type "new"
        draft[id] = {
          item: deepMergeKeepArrays(
            (existing?.item ?? {}) as T,
            item as T,
          ) as Draft<T>,
          type: "new",
        } as unknown as Draft<EditorChanges<T>[string]>;
      } else {
        // For updates or new entries, merge with existing data (if any)
        draft[id] = {
          item: deepMergeKeepArrays(
            (existing?.item ?? {}) as DeepPartial<T>,
            item as DeepPartial<T>,
          ) as Draft<DeepPartial<T>>,
          type: "updated",
        } as unknown as Draft<EditorChanges<T>[string]>;
      }
    }
  });
};
